import React, { useEffect, useState } from "react";
import { api, fmtDate, formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, MapPin } from "lucide-react";
import StatusBadge from "@/components/crm/StatusBadge";
import { SearchBox, DeleteButton, matchesQuery } from "@/components/crm/RecordActions";
import FilterBar, { FilterSelect, inDateRange } from "@/components/crm/Filters";
import { PhotoField, PhotoGallery } from "@/components/crm/PhotoUpload";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const TYPES = ["AMC","Repair","Cleaning","Panel Fault","Inverter Fault","Wiring Fault","Other"];
const STATUS = ["Pending","In Progress","Completed"];
const empty = {
  customer_id:"", service_type:"AMC", technician_id:"", visit_date:"",
  problem:"", work_performed:"", parts_used:"",
  before_photo:null, after_photo:null, site_visit_photos:[],
  panel_serial_no:"", panel_serial_photo:null,
  inverter_serial_no:"", inverter_serial_photo:null,
  gps_lat:null, gps_lng:null, gps_photo:null,
  next_service_date:"", status:"Pending",
};

export default function Services() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [technicians, setTechnicians] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [search, setSearch] = useState("");
  const [fFrom, setFFrom] = useState("");
  const [fTo, setFTo] = useState("");
  const [fType, setFType] = useState("__all__");
  const [fStatus, setFStatus] = useState("__all__");
  const [fTech, setFTech] = useState("__all__");

  const load = async () => {
    setItems((await api.get("/services")).data);
    if (user.role !== "technician") {
      setCustomers((await api.get("/customers")).data);
      if (user.role === "admin") {
        try { setTechnicians((await api.get("/users")).data.filter(u=>u.role==="technician")); } catch {}
      }
    }
  };
  useEffect(()=>{ load(); },[]);

  const save = async () => {
    try {
      if (editId) await api.patch(`/services/${editId}`, form);
      else await api.post("/services", form);
      toast.success("Saved");
      setOpen(false); setForm(empty); setEditId(null); load();
    } catch(e){ toast.error(formatError(e.response?.data?.detail)); }
  };

  const edit = (s) => { setForm({...empty, ...s}); setEditId(s.id); setOpen(true); };

  const del = async (s) => {
    try { await api.delete(`/services/${s.id}`); toast.success(`Service ${s.service_no} deleted`); load(); }
    catch(e){ toast.error(formatError(e.response?.data?.detail)); }
  };

  const canCreate = user.role === "admin" || user.role === "sales";
  const visible = items
    .filter((s) => matchesQuery(s, search))
    .filter((s) => inDateRange(s.created_at, fFrom, fTo))
    .filter((s) => fType === "__all__" || s.service_type === fType)
    .filter((s) => fStatus === "__all__" || s.status === fStatus)
    .filter((s) => fTech === "__all__" || s.technician_id === fTech);

  const captureGps = () => {
    if (!navigator.geolocation) { toast.error("GPS not available"); return; }
    navigator.geolocation.getCurrentPosition(
      (p) => { setForm(f => ({...f, gps_lat:p.coords.latitude, gps_lng:p.coords.longitude})); toast.success(`GPS captured (${p.coords.latitude.toFixed(4)}, ${p.coords.longitude.toFixed(4)})`); },
      () => toast.error("Unable to capture GPS"),
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  return (
    <div className="space-y-5" data-testid="services-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-3xl font-extrabold text-slate-900" style={{fontFamily:'Outfit'}}>Service <span className="text-slate-400 text-lg font-medium">/ सर्विस</span></h1>
          <p className="text-slate-500 text-sm">Track technician visits, repairs & AMCs. Capture photos + GPS onsite.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <SearchBox value={search} onChange={setSearch} placeholder="Search customer, service #..." testid="services-search-input" />
          <Dialog open={open} onOpenChange={(v)=>{setOpen(v); if(!v){setForm(empty); setEditId(null);}}}>
            {canCreate && (
            <DialogTrigger asChild>
              <Button className="bg-amber-500 hover:bg-amber-600 text-white" data-testid="add-service-btn"><Plus className="w-4 h-4 mr-1"/>New Service</Button>
            </DialogTrigger>
            )}
            <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
              <DialogHeader><DialogTitle>{editId?"Update":"New"} Service Ticket</DialogTitle></DialogHeader>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="sm:col-span-2">
                  <Label className="text-xs font-semibold text-slate-600">Customer*</Label>
                  <Select value={form.customer_id} onValueChange={v=>setForm({...form, customer_id:v})}>
                    <SelectTrigger data-testid="service-customer-select"><SelectValue placeholder="Select"/></SelectTrigger>
                    <SelectContent>{customers.map(c=><SelectItem key={c.id} value={c.id}>{c.name} — {c.mobile}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label className="text-xs font-semibold text-slate-600">Service Type</Label>
                  <Select value={form.service_type} onValueChange={v=>setForm({...form, service_type:v})}>
                    <SelectTrigger><SelectValue/></SelectTrigger>
                    <SelectContent>{TYPES.map(t=><SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label className="text-xs font-semibold text-slate-600">Assign Technician</Label>
                  <Select value={form.technician_id||""} onValueChange={v=>setForm({...form, technician_id:v})}>
                    <SelectTrigger data-testid="service-technician-select"><SelectValue placeholder="Select technician"/></SelectTrigger>
                    <SelectContent>{technicians.map(t=><SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label className="text-xs font-semibold text-slate-600">Visit Date</Label><Input type="date" value={form.visit_date||""} onChange={e=>setForm({...form, visit_date:e.target.value})}/></div>
                <div><Label className="text-xs font-semibold text-slate-600">Status</Label>
                  <Select value={form.status} onValueChange={v=>setForm({...form, status:v})}>
                    <SelectTrigger><SelectValue/></SelectTrigger>
                    <SelectContent>{STATUS.map(t=><SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="sm:col-span-2"><Label className="text-xs font-semibold text-slate-600">Problem Reported</Label><Textarea rows={2} value={form.problem} onChange={e=>setForm({...form, problem:e.target.value})}/></div>
                <div className="sm:col-span-2"><Label className="text-xs font-semibold text-slate-600">Work Performed</Label><Textarea rows={2} value={form.work_performed} onChange={e=>setForm({...form, work_performed:e.target.value})}/></div>
                <div><Label className="text-xs font-semibold text-slate-600">Parts Used</Label><Input value={form.parts_used} onChange={e=>setForm({...form, parts_used:e.target.value})}/></div>
                <div><Label className="text-xs font-semibold text-slate-600">Next Service Date</Label><Input type="date" value={form.next_service_date||""} onChange={e=>setForm({...form, next_service_date:e.target.value})}/></div>
              </div>

              <div className="mt-3 text-xs font-bold uppercase tracking-widest text-slate-500 border-b pb-1">Site Serial Numbers & Photos</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-2">
                <div>
                  <Label className="text-xs font-semibold text-slate-600">Panel Serial No.</Label>
                  <Input value={form.panel_serial_no||""} onChange={e=>setForm({...form, panel_serial_no:e.target.value})} placeholder="e.g. WSMD-585-12345" data-testid="panel-serial-input"/>
                  <div className="mt-2"><PhotoField label="Panel Serial Photo" value={form.panel_serial_photo} onChange={(p)=>setForm({...form, panel_serial_photo:p})} testid="panel-serial-photo" /></div>
                </div>
                <div>
                  <Label className="text-xs font-semibold text-slate-600">Inverter Serial No.</Label>
                  <Input value={form.inverter_serial_no||""} onChange={e=>setForm({...form, inverter_serial_no:e.target.value})} placeholder="e.g. SG15KTL-M-6789" data-testid="inverter-serial-input"/>
                  <div className="mt-2"><PhotoField label="Inverter Serial Photo" value={form.inverter_serial_photo} onChange={(p)=>setForm({...form, inverter_serial_photo:p})} testid="inverter-serial-photo" /></div>
                </div>
              </div>

              <div className="mt-3 text-xs font-bold uppercase tracking-widest text-slate-500 border-b pb-1">GPS Location</div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 items-end mt-2">
                <div><Label className="text-xs font-semibold text-slate-600">Latitude</Label><Input type="number" step="0.000001" value={form.gps_lat??""} onChange={e=>setForm({...form, gps_lat:parseFloat(e.target.value)||null})} data-testid="gps-lat-input"/></div>
                <div><Label className="text-xs font-semibold text-slate-600">Longitude</Label><Input type="number" step="0.000001" value={form.gps_lng??""} onChange={e=>setForm({...form, gps_lng:parseFloat(e.target.value)||null})} data-testid="gps-lng-input"/></div>
                <Button type="button" variant="outline" onClick={captureGps} data-testid="capture-gps-btn"><MapPin className="w-4 h-4 mr-1 text-emerald-600"/>Capture GPS</Button>
              </div>
              <div className="mt-2"><PhotoField label="GPS-tagged Site Photo" value={form.gps_photo} onChange={(p,g)=>setForm({...form, gps_photo:p, ...(g?{gps_lat:g.lat, gps_lng:g.lng}:{})})} testid="gps-photo" withGps /></div>

              <div className="mt-3 text-xs font-bold uppercase tracking-widest text-slate-500 border-b pb-1">Before / After & Site Photos</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-2">
                <PhotoField label="Before Photo" value={form.before_photo} onChange={(p)=>setForm({...form, before_photo:p})} testid="before-photo" />
                <PhotoField label="After Photo" value={form.after_photo} onChange={(p)=>setForm({...form, after_photo:p})} testid="after-photo" />
              </div>
              <div className="mt-2"><PhotoGallery label="Site Visit Photos" value={form.site_visit_photos||[]} onChange={(arr)=>setForm({...form, site_visit_photos:arr})} testid="site-visit-photos" /></div>

              <DialogFooter>
                <Button variant="outline" onClick={()=>setOpen(false)}>Cancel</Button>
                <Button className="bg-amber-500 hover:bg-amber-600" onClick={save} disabled={!form.customer_id} data-testid="save-service-btn">Save</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <FilterBar from={fFrom} to={fTo} onFrom={setFFrom} onTo={setFTo}
        onClear={()=>{setFFrom("");setFTo("");setFType("__all__");setFStatus("__all__");setFTech("__all__");setSearch("");}}>
        <FilterSelect value={fType} onChange={setFType} placeholder="Type" testid="filter-type"
          options={TYPES.map(t=>({value:t,label:t}))} />
        <FilterSelect value={fStatus} onChange={setFStatus} placeholder="Status" testid="filter-status"
          options={STATUS.map(s=>({value:s,label:s}))} />
        {user.role === "admin" && (
          <FilterSelect value={fTech} onChange={setFTech} placeholder="Technician" testid="filter-technician"
            options={technicians.map(t=>({value:t.id,label:t.name}))} />
        )}
      </FilterBar>

      <Card><CardContent className="p-0">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Service #</TableHead><TableHead>Customer</TableHead>
            <TableHead>Type</TableHead><TableHead>Visit</TableHead>
            <TableHead>Status</TableHead><TableHead>Next</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {visible.length===0 && <TableRow><TableCell colSpan={7} className="text-center py-10 text-slate-400">{search ? `No services matching "${search}"` : "No service tickets."}</TableCell></TableRow>}
            {visible.map(s=>(
              <TableRow key={s.id} data-testid={`service-row-${s.service_no}`}>
                <TableCell className="font-mono text-xs">{s.service_no}</TableCell>
                <TableCell className="font-medium">{s.customer_name}<div className="text-xs text-slate-500">{s.customer_mobile}</div></TableCell>
                <TableCell><span className="text-xs bg-slate-100 rounded-full px-2 py-0.5">{s.service_type}</span></TableCell>
                <TableCell className="text-xs">{fmtDate(s.visit_date)}</TableCell>
                <TableCell><StatusBadge value={s.status}/></TableCell>
                <TableCell className="text-xs">{fmtDate(s.next_service_date)}</TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-1">
                    <Button size="sm" variant="ghost" onClick={()=>edit(s)} data-testid={`edit-service-${s.service_no}`}>{user.role==="technician"?"Update":"Edit"}</Button>
                    {user?.role === "admin" && (
                      <DeleteButton title={`Delete Service ${s.service_no}?`} description={`This will permanently delete this service ticket for ${s.customer_name}. This action cannot be undone.`} onConfirm={()=>del(s)} testid={`delete-service-${s.service_no}`} />
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent></Card>
    </div>
  );
}
